import React, { useEffect, useState } from 'react';

// Third Party Libraries
// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Date Range Picker
import DatePicker from '~/components/General/OlcDatePicker';
import { addDays } from 'date-fns';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { createApiKey, fetchApiKeys } from '~/redux/actions/apiKeys-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';
import { fetchSources } from '~/redux/actions/sources-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { validateForm, realTimeValidation, resetValidation } from '~/utils/formik-validation';
import { dateProtector, handleRawChangeForDatePicker } from '~/utils/date-format';
import { trimName } from '~/utils/helperFunctions';
import { DATE_REGEX, DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE } from '~/utils/constants';

// Components
import OlcDropdown from '~/components/General/OlcDropdown';
import ModalInput from '~/components/Contacts/CreateContact/ModalInput/index.jsx';
import LimitReach from '~/components/General/LimitModal';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import Typography from '@mui/material/Typography';
import { Box, InputLabel, CircularProgress, Tooltip, InputAdornment } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

// Icons
import { InfoCIcon, MultiSelectArrowCIcon } from '~/assets/images';

// Styles
import './styles.scss';
import OlcButton from '~/components/General/OlcButton';

const CreateApiModal = (props) => {
  const formId = 'create-api-key-form';
  const [startDate, setStartDate] = useState(null);
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [formSelect, setFormSelect] = useState({
    source: '',
  });
  const [sourceOptions, setSourceOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limitModal, setLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const { openModal, handleCloseModal, updatePagination } = props;

  const dispatch = useDispatch();
  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .trim()
      .required(MESSAGES.SETTINGS.API_KEYS.CREATE.NAME_REQUIRED)
      .min(1, MESSAGES.SETTINGS.API_KEYS.CREATE.NAME_REQUIRED)
      .max(255, MESSAGES.SETTINGS.API_KEYS.CREATE.NAME_LESS_255),
    source: yup.string().required(MESSAGES.SETTINGS.API_KEYS.CREATE.SOURCE_REQUIRED),
    ghlApiKey: yup.string().optional(),
    shopifyStoreName: yup.string().when('source', {
      is: (val) => val === 'Shopify',
      then: (schema) =>
        schema.required(MESSAGES.SETTINGS.API_KEYS.CREATE.SHOPIFY_STORE_NAME_REQUIRED),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      source: '',
      ghlApiKey: '',
      shopifyStoreName: '',
    },
    validationSchema: validationSchema,
  });

  const onClose = () => {
    handleCloseModal();
    formik.handleReset();
    resetValidation();
    setStartDate(null);
    setFormSelect({
      source: '',
    });
    formik.setTouched({});
  };

  const handleFormSelect = (field, newValue) => {
    const value = newValue?.value || newValue?.title || '';
    formik.setFieldValue(field, value);
    setFormSelect((prevState) => ({
      ...prevState,
      [field]: newValue,
    }));
  };

  const handleClearField = (field) => {
    formik.setFieldValue(field, '');
    // Mark as touched so validation shows on blur
    formik.setFieldTouched(field, true);
  };

  // Helper function to create endAdornment with cross button
  const getClearButtonAdornment = (fieldName) => {
    const fieldValue = formik.values[fieldName];
    return (
      fieldValue?.trim() && (
        <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
          <IconButton
            size="small"
            onClick={() => handleClearField(fieldName)}
            edge="end"
            sx={{ padding: '4px' }}
            className="cpClearInputBtn smallClearIcon"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      )
    );
  };

  const createNewApi = async (event) => {
    try {
      event.preventDefault();
      const isValid = validateForm(formik);
      if (!isValid) {
        return;
      }
      setLoading(true);
      const payload = {
        name: trimName(formik.values.name),
        source: formik.values.source,
        crmApiKey: formik.values.ghlApiKey,
        expiredAt: startDate?.toISOString()?.slice(0, 10),
        isSecretRequired: false,
        shopifyStoreName: formik.values.shopifyStoreName,
      };
      const response = await dispatch(createApiKey(payload));
      if (response?.status === 418) {
        const errorMessage = response?.data?.message || MESSAGES.SETTINGS.API_KEYS.NOT_ALLOWED;
        onClose();
        setLimitMessage(errorMessage);
        setLimitModal(true);
        return;
      } else if (response.data.message == MESSAGES.SETTINGS.API_KEYS.RESPONSE_ALREADY_EXIST) {
        formik.setErrors({
          name: MESSAGES.SETTINGS.API_KEYS.NAME_ALREADY_EXIST,
          source: MESSAGES.SETTINGS.API_KEYS.SOURCE_ALREADY_EXIST,
        });
        formik.touched['name'] = true;
        formik.touched['source'] = true;
      } else if (response.data.message == MESSAGES.SETTINGS.API_KEYS.SOURCE_ALREADY_EXIST_IN_DB) {
        formik.setErrors({
          source: MESSAGES.SETTINGS.API_KEYS.SOURCE_ALREADY_EXIST,
        });
        formik.touched['source'] = true;
      } else if (response.data.message == MESSAGES.SETTINGS.API_KEYS.GHL_API_KEY_INVALID) {
        formik.setErrors({
          ghlApiKey: MESSAGES.SETTINGS.API_KEYS.GHL_API_KEY_INVALID,
        });
        formik.touched['ghlApiKey'] = true;
      } else if (response.data.message == 'Shopify store name must end with myshopify.com') {
        formik.setErrors({
          shopifyStoreName: response.data.message,
        });
        formik.touched['shopifyStoreName'] = true;
      } else {
        response?.data?.message && dispatch(success(response?.data?.message));
        updatePagination(DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE);
        dispatch(fetchApiKeys(DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE));
        onClose();
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
      return error;
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const fetchSourcesData = async () => {
    const response = await dispatch(fetchSources());
    if (response?.status === 200) {
      const mappedOptions = response?.data?.data?.map((item) => ({
        id: item.id,
        title: item.name,
        label: item.name,
        value: item.key,
      }));
      setSourceOptions(mappedOptions);
    }
  };

  useEffect(() => {
    fetchSourcesData();
  }, []);

  return (
    <>
      <LimitReach
        open={limitModal}
        onClose={() => {
          setLimitModal(false);
          setTimeout(() => setLimitMessage(''), 100);
        }}
        message={limitMessage}
        resourceType="api-keys"
      />
      <OlcFormModal
        open={openModal}
        onClose={onClose}
        title={MESSAGES.SETTINGS.API_KEYS.CREATE.TITLE}
        className="createApiModalWrapper"
        footer={
          <>
            <OlcButton variant="secondary" type="button" onClick={onClose} disabled={loading}>
              {MESSAGES.SETTINGS.API_KEYS.CANCEL_BUTTON}
            </OlcButton>
            <OlcButton
              variant="primary"
              type="submit"
              form={formId}
              onClick={() => validateForm(formik)}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress
                  sx={{
                    color: 'white',
                    width: '20px !important',
                    height: '20px !important',
                  }}
                />
              ) : (
                MESSAGES.SETTINGS.API_KEYS.SUBMIT_BUTTON
              )}
            </OlcButton>
          </>
        }
      >
        <form
          id={formId}
          className="updateFormWrapper apiKeyModalBody"
          onSubmit={createNewApi}
          noValidate
        >
          {/* Name of API Key */}
          <Box className="editNameInputsWrapper">
            <ModalInput
              label={
                <>
                  {MESSAGES.SETTINGS.API_KEYS.CREATE.NAME_LABEL}
                  <span className="olcRequired">*</span>
                </>
              }
              type="text"
              name="name"
              formik={formik}
              realTimeValidation={realTimeValidation}
              value={formik.values.name.trimStart()}
              isCreate={true}
              placeholder="Name"
              endAdornment={getClearButtonAdornment('name')}
            />
          </Box>
          {/* Source of API Key */}
          <Box className="editNameInputsWrapper">
            <Typography className="inputLabel">
              {MESSAGES.SETTINGS.API_KEYS.CREATE.SOURCE_LABEL}
              <span className="olcRequired">*</span>
            </Typography>
            <OlcDropdown
              placeholder="Source"
              options={sourceOptions}
              selectedValue={formSelect?.source}
              setSelectedValue={(val) => handleFormSelect('source', val)}
              search={() => {}}
              updateErrors={() => {}}
              filterAutoComplete
              className="newAutocompleteDesign olcNewDropdown"
              newDesign={true}
              error={formik.touched.source && formik.errors.source}
              readOnlyInput={true}
              disableClearable={true}
            />
            {formik.touched.source && formik.errors.source ? (
              <Typography className="errorMessage">
                <sup>*</sup>
                {formik.errors.source}
              </Typography>
            ) : null}
          </Box>
          {formik.values.source && formik.values.source.includes('GoHighLevel') && (
            /* GHL API Key for Webhook (Optional) */
            <Box className="editNameInputsWrapper">
              <ModalInput
                label={
                  <>
                    {MESSAGES.SETTINGS.API_KEYS.CREATE.GHL_API_KEY_LABEL}
                    <Tooltip
                      title={MESSAGES.SETTINGS.API_KEYS.CREATE.GHL_API_KEY_TOOLTIP}
                      placement="bottom-start"
                    >
                      <span
                        className="tooltipIcon"
                        style={{ marginLeft: '8px', cursor: 'pointer' }}
                      >
                        <img src={InfoCIcon} alt="infoIcon" />
                      </span>
                    </Tooltip>
                  </>
                }
                type="text"
                name="ghlApiKey"
                formik={formik}
                realTimeValidation={realTimeValidation}
                value={formik.values.ghlApiKey.trimStart()}
                isCreate={true}
                placeholder="GHL API Key"
                endAdornment={getClearButtonAdornment('ghlApiKey')}
              />
            </Box>
          )}
          {formik.values.source && formik.values.source.includes('Shopify') && (
            /* Shopify Store Name (Required for Shopify source) */
            <Box className="editNameInputsWrapper">
              <ModalInput
                label={MESSAGES.SETTINGS.API_KEYS.CREATE.SHOPIFY_STORE_NAME_LABEL}
                type="text"
                name="shopifyStoreName"
                formik={formik}
                realTimeValidation={realTimeValidation}
                value={formik.values.shopifyStoreName.trimStart()}
                isCreate={true}
                placeholder="Shopify Store Name"
                endAdornment={getClearButtonAdornment('shopifyStoreName')}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  color: '#303030',
                  fontFamily: 'Inter',
                  mt: 1,
                  fontWeight: 400,
                }}
              >
                <span style={{ fontWeight: 600 }}>Store address:</span> (e.g.,
                store-name.myshopify.com)
              </Typography>
            </Box>
          )}
          {/* Expiry Date of API Key */}
          <Box className="mailedDate newDatePicker updatedDesign">
            <Box className="selectDate" sx={{ maxWidth: '100% !important' }}>
              <InputLabel className="dateLabel newfieldsLabel">
                {MESSAGES.SETTINGS.API_KEYS.CREATE.DATE_LABEL}
              </InputLabel>
              <img
                src={MultiSelectArrowCIcon}
                alt="arrow"
                style={{
                  transform: isDatepickerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
              <DatePicker
                toggleCalendarOnIconClick={true}
                selected={startDate}
                minDate={addDays(new Date(), 1)}
                onChange={(update) => {
                  dateProtector(update);
                  setStartDate(update);
                }}
                onChangeRaw={(e) => {
                  if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                    handleRawChangeForDatePicker(e, setStartDate);
                  } else {
                    e.preventDefault();
                  }
                }}
                onFocus={() => setIsDatepickerOpen(true)}
                onBlur={() => setIsDatepickerOpen(false)}
                placeholderText={MESSAGES.SETTINGS.API_KEYS.CREATE.DATE_PLACEHOLDER}
              />
            </Box>
          </Box>
        </form>
      </OlcFormModal>
    </>
  );
};

export default CreateApiModal;
